import { useBranchTimezone } from "../api/use-branch-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  HrCertificate,
  HrTeam,
  SalaryPerson,
  UpdateContract,
  UpdatePersonal,
} from "@dentacore/shared-types";

import { listSalaries } from "../api/accounting";
import { useModules } from "../shell/modules-context";
import {
  backAtWork,
  readTeam,
  recordSick,
  renewCertificate,
  updateContract,
  updatePersonal,
} from "../api/hr";
import { describe } from "../settings/use-writer";
import type { Writer } from "../settings/use-writer";
import { Pager, useFitPages } from "../ui/paging";
import { daysText, hoursOf, initialsOf, localToday, roleLabel } from "./roster";
import styles from "./staff.module.css";
import { byNeed, CONTRACT_LABELS, matches, toneOf } from "./team";
import own from "./team.module.css";
import { ChangeContractDialog, ChangePersonalDialog, RenewDialog } from "./team-dialogs";
import { Mark } from "./team-mark";
import { TeamPanel } from "./team-panel";

/**
 * Staff → Team (STORY-181c; HR-D1, HR-D3, HR-D8, HR-D13–HR-D15).
 *
 * **Who is in, whose record wants an eye, and each person's contract** — for
 * whoever holds `hr.team.view`. Today's figures are about people only (HR-D14);
 * a location whose front desk is all out is named (HR-D13). The list puts
 * whoever needs attention first and pages to the room it has (PF4): nothing on
 * this tab scrolls. A person opens beside it, and on a phone over it.
 *
 * Every write goes through the page's writer, so a refusal reads like every
 * other one on Staff, and every write re-reads the team — a sick day moves the
 * figures, the row and the panel at once.
 */
export function TeamView({
  meId,
  currency,
  can,
  writer,
}: {
  meId: string | null;
  currency: string;
  can: (
    permission:
      | "hr.record.manage"
      | "hr.absence.record"
      | "hr.compliance.manage"
      | "accounting.salaries.view",
  ) => boolean;
  writer: Pick<Writer, "busy" | "run" | "fail">;
}): React.JSX.Element {
  const { busy, run, fail } = writer;
  const timezone = useBranchTimezone();
  const today = localToday(timezone);

  const [team, setTeam] = useState<HrTeam | null>(null);
  const [salaries, setSalaries] = useState<readonly SalaryPerson[] | null>(null);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [renewing, setRenewing] = useState<HrCertificate | null>(null);

  // Salaries are the clinic's own economics (MS-D1): Accounting's, so the Pay
  // tab is there only with the add-on as well as the permission.
  const { has } = useModules();
  // Pay is the salaries right since QA4-D8, not profit.
  const seesPay = can("accounting.salaries.view") && has("ACCOUNTING");

  const reload = useCallback(async (signal?: AbortSignal) => {
    setTeam(await readTeam(signal));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "Could not read the team."));
    });
    return () => controller.abort();
  }, [reload, fail]);

  useEffect(() => {
    if (!seesPay) return;
    const controller = new AbortController();
    // Quiet: Pay says "No salary recorded" rather than blocking the team.
    listSalaries(controller.signal)
      .then(setSalaries)
      .catch(() => undefined);
    return () => controller.abort();
  }, [seesPay]);

  const rows = useMemo(
    () =>
      byNeed(
        (team?.people ?? []).filter((person) => matches(person, query)),
        today,
      ),
    [team, query, today],
  );
  const selected = team?.people.find((person) => person.userId === selectedId) ?? null;
  const open = selected !== null;

  const fit = useFitPages({
    keys: rows.map((row) => row.person.userId),
    estimate: () => 57,
    gap: 0,
    resetOn: query,
    follow: selectedId === null ? null : { key: selectedId },
  });
  const [start, end] = fit.range;

  // Escape closes the person, as it closes every panel on Staff.
  useEffect(() => {
    if (!open || changing || editingPersonal || renewing !== null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setSelectedId(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, changing, editingPersonal, renewing]);

  function saveContract(change: UpdateContract): void {
    if (selected === null) return;
    if (Object.keys(change).length === 0) {
      setChanging(false);
      return;
    }
    void run(
      () => updateContract(selected.userId, change),
      () => reload(),
      "Contract saved.",
    ).then((ok) => {
      if (ok) setChanging(false);
    });
  }

  function savePersonal(change: UpdatePersonal): void {
    if (selected === null) return;
    if (Object.keys(change).length === 0) {
      setEditingPersonal(false);
      return;
    }
    void run(
      () => updatePersonal(selected.userId, change),
      () => reload(),
      "Personal details saved.",
    ).then((ok) => {
      if (ok) setEditingPersonal(false);
    });
  }

  function renew(expiresOn: string | undefined, file: File | null): void {
    if (renewing === null) return;
    void run(
      () => renewCertificate(renewing.id, expiresOn, file),
      () => reload(),
      `${renewing.name} renewed.`,
    ).then((ok) => {
      if (ok) setRenewing(null);
    });
  }

  const figures = team?.today;

  return (
    <div className={own.column} data-testid="team-view">
      <div className={own.figures}>
        {figures === undefined ? (
          <span>Reading&hellip;</span>
        ) : (
          <>
            <span data-testid="team-in">
              <b>
                {figures.in} of {figures.total}
              </b>{" "}
              in today
            </span>
            <span
              className={figures.onLeave > 0 ? own.figureWarm : undefined}
              data-testid="team-on-leave"
            >
              <b>{figures.onLeave}</b> on leave
            </span>
            <span className={figures.sick > 0 ? own.figureWarm : undefined} data-testid="team-sick">
              <b>{figures.sick}</b> off sick
            </span>
          </>
        )}
        <input
          type="search"
          className={`${styles.search} ${own.searchEnd}`}
          placeholder="Search the team"
          aria-label="Search the team"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-testid="team-search"
        />
      </div>

      {figures !== undefined && figures.noFrontDeskAt.length > 0 && (
        <p className={own.deskWarning} role="status" data-testid="no-front-desk">
          No front desk at {figures.noFrontDeskAt.map((row) => row.name).join(", ")} today. Nothing
          is cancelled &mdash; the desk is simply unstaffed.
        </p>
      )}

      <div className={`${styles.body} ${own.bodyFlush}`}>
        <section className={open ? `${own.card} ${own.narrow}` : own.card} aria-label="The team">
          <div className={`${own.cells} ${own.head}`}>
            <span className={own.headTitle}>
              <b>The team</b>
              <span className={own.count}>{rows.length}</span>
            </span>
            <span>Contract</span>
            <span>Annual leave</span>
            {!open && <span>Needs attention</span>}
          </div>

          <div className={own.area} ref={fit.areaRef} data-testid="team-list">
            {team !== null && rows.length === 0 && (
              <p className={own.empty}>
                {query.trim() === "" ? "Nobody is active in this clinic." : "Nobody matches that."}
              </p>
            )}
            {rows.slice(start, end).map(({ person, reasons }) => {
              const tone = toneOf(reasons);
              const first = reasons[0];
              const left = person.leave.remainingDays;
              const [given = "", ...family] = person.name.split(" ");
              const away =
                person.today === "SICK"
                  ? " · off sick"
                  : person.today === "ON_LEAVE"
                    ? " · on leave"
                    : "";
              return (
                <button
                  key={person.userId}
                  type="button"
                  ref={fit.measure(person.userId)}
                  className={`${own.row} ${own.cells} ${selectedId === person.userId ? own.rowOn : ""}`}
                  aria-pressed={selectedId === person.userId}
                  onClick={() => setSelectedId(selectedId === person.userId ? null : person.userId)}
                  data-testid="team-row"
                  data-person={person.userId}
                >
                  <span className={own.who}>
                    <Mark tone={tone} />
                    <span className={own.face} data-role={person.role} aria-hidden="true">
                      {initialsOf(given, family.join(" "))}
                    </span>
                    <span className={own.name}>
                      <b>{person.name}</b>
                      <span className={own.sub}>
                        {roleLabel(person.role)}
                        {away}
                        {/* With the panel open the reason column steps aside, so it moves here. */}
                        {open && first !== undefined && first.text !== "Off sick" && (
                          <span className={first.tone === "calm" ? undefined : own.warmText}>
                            {` · ${first.text}`}
                          </span>
                        )}
                      </span>
                    </span>
                  </span>
                  <span className={own.cell}>
                    {person.contract.contractType === null
                      ? "—"
                      : CONTRACT_LABELS[person.contract.contractType]}
                    <span className={own.sub}>
                      {person.contract.contractedMinutes === null
                        ? "no hours set"
                        : `${hoursOf(person.contract.contractedMinutes)} a week`}
                    </span>
                  </span>
                  <span className={own.cell}>
                    {left === null ? (
                      <>
                        —<span className={own.sub}>no allowance set</span>
                      </>
                    ) : left < 0 ? (
                      <>
                        <span className={own.warmText}>{daysText(-left)} days</span>
                        <span className={own.sub}>over the allowance</span>
                      </>
                    ) : (
                      <>
                        {daysText(left)} days<span className={own.sub}>annual leave left</span>
                      </>
                    )}
                  </span>
                  {!open && (
                    <span className={own.cell} data-testid="team-need">
                      {first === undefined ? (
                        <span className={own.sub}>—</span>
                      ) : (
                        <>
                          <span className={first.tone === "calm" ? undefined : own.warmText}>
                            {first.text}
                          </span>
                          <span className={own.sub}>
                            {first.sub}
                            {reasons.length > 1 && ` · +${String(reasons.length - 1)} more`}
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className={own.foot}>
            <Pager
              page={fit.page}
              count={fit.pages.length}
              onPage={fit.setPage}
              from={start + 1}
              to={end}
              total={rows.length}
              noun="people"
              testId="team-pager"
            />
          </div>
        </section>

        {selected !== null && (
          <>
            <button
              type="button"
              className={own.scrim}
              aria-label="Close"
              tabIndex={-1}
              onClick={() => setSelectedId(null)}
            />
            <TeamPanel
              person={selected}
              today={today}
              isSelf={selected.userId === meId}
              canManageContract={can("hr.record.manage")}
              canRecordAbsence={can("hr.absence.record")}
              canRenew={can("hr.compliance.manage")}
              pay={
                seesPay
                  ? (salaries?.find((row) => row.userId === selected.userId) ?? null)
                  : undefined
              }
              currency={currency}
              busy={busy}
              onClose={() => setSelectedId(null)}
              onChangeContract={() => setChanging(true)}
              onChangePersonal={() => setEditingPersonal(true)}
              onSick={() => {
                void run(
                  () => recordSick(selected.userId),
                  () => reload(),
                  `${selected.name} is recorded off sick today.`,
                );
              }}
              onBackAtWork={() => {
                void run(
                  () => backAtWork(selected.userId),
                  () => reload(),
                  `${selected.name} is back at work.`,
                );
              }}
              onRenew={setRenewing}
            />
          </>
        )}
      </div>

      {changing && selected !== null && (
        <ChangeContractDialog
          name={selected.name}
          contract={selected.contract}
          busy={busy}
          onSave={saveContract}
          onClose={() => setChanging(false)}
        />
      )}

      {editingPersonal && selected !== null && (
        <ChangePersonalDialog
          name={selected.name}
          personal={selected.personal}
          today={today}
          busy={busy}
          onSave={savePersonal}
          onClose={() => setEditingPersonal(false)}
        />
      )}

      {renewing !== null && selected !== null && (
        <RenewDialog
          name={selected.name}
          certificate={renewing}
          countsAtOnce
          busy={busy}
          onSave={renew}
          onClose={() => setRenewing(null)}
        />
      )}
    </div>
  );
}
